// =============================================================================
// io/sync/saveMidSessionNotes.ts — the running log on a journal day.
// -----------------------------------------------------------------------------
// ONE column: `mid_session_notes`. Its own module rather than an argument on the
// recap write, for the reason D62 gives and `saveRecordings` states first — a
// writer owns a fixed column set, and widening the recap save to carry notes
// would make the review form the owner of a list it does not render.
//
// The old client autosaves the whole entry on every keystroke, so a note rides
// the same write as the prose. This client cannot do that (the typed model is
// lossy), so each owned thing gets its own narrow statement.
//
// ── ⚠ THE LIST IS SENT WHOLE ──
//
// Safe only because it is READ whole: `loadJournal` takes every element and the
// Session view renders every element, so the array in memory is the array on
// disk and sending it back cannot drop a note the client never saw. Same
// condition as the recordings write, same consequence if the read ever narrows.
//
// ⚠ `notesToRows` sends `{ time, text }` and drops the `id` the reader invents.
// See its header — writing an invented key back is the D62 sin in reverse.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { notesToRows } from '../../domain/journal';
import type { MidSessionNote } from '../../domain/journal';
import { reportFailureAs } from '../diagnostics/errorLog';

/** Every column this module writes. Fixed, and asserted by a test. */
export const NOTES_COLUMNS = ['user_id', 'date', 'mid_session_notes'] as const;

export interface SaveNotesArgs {
  sb: SupabaseClient;
  userId: string;
  date: string;
  notes: readonly MidSessionNote[];
  baselineUpdatedAt: string | null;
}

export interface SaveNotesResult {
  updatedAt: string | null;
  error: string | null;
  heldStale: boolean;
}

export function notesRow(
  date: string, notes: readonly MidSessionNote[], userId: string,
): Record<string, unknown> {
  return { user_id: userId, date, mid_session_notes: notesToRows(notes) };
}

export async function saveMidSessionNotes(
  args: SaveNotesArgs,
): Promise<SaveNotesResult> {
  const { sb, userId, date, notes, baselineUpdatedAt } = args;

  // ⚠ FAIL-OPEN, identical to the other journal writers. A guard that blocked a
  // save it could not verify would lose real work on a transient blip.
  if (baselineUpdatedAt) {
    const { data, error } = await sb
      .from('journal_entries')
      .select('date, updated_at')
      .eq('user_id', userId)
      .eq('date', date);

    if (!error && Array.isArray(data) && data.length === 1) {
      const cloud = (data[0] as unknown as Record<string, unknown>)['updated_at'];
      if (typeof cloud === 'string'
        && new Date(cloud).getTime() > new Date(baselineUpdatedAt).getTime()) {
        return {
          updatedAt: null,
          heldStale: true,
          error:
            `This day was changed somewhere else after you loaded it — probably the ` +
            `other client or another tab. Nothing was saved, so neither version is ` +
            `lost. Reload to see the newer copy, then re-add your note.`,
        };
      }
    }
  }

  const { data, error } = await sb
    .from('journal_entries')
    .upsert(notesRow(date, notes, userId), { onConflict: 'user_id,date' })
    .select('date, updated_at');

  if (error) {
    return {
      updatedAt: null,
      heldStale: false,
      error: reportFailureAs('save mid-session notes', error, `Save failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length !== 1) {
    return {
      updatedAt: null,
      heldStale: false,
      error:
        `The save returned ${Array.isArray(data) ? data.length : 'no'} rows instead of one, `
        + `so it cannot be confirmed. Reload before editing again.`,
    };
  }

  const ts = (data[0] as unknown as Record<string, unknown>)['updated_at'];
  return { updatedAt: typeof ts === 'string' ? ts : null, heldStale: false, error: null };
}
